import {
  useAudioRecorder,
  RecordingPresets,
  setAudioModeAsync,
} from 'expo-audio';
import * as FileSystem from 'expo-file-system/legacy';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, Animated, TouchableOpacity } from 'react-native';
import { useToast } from 'react-native-toast-notifications';
import uuid from 'react-native-uuid';

import { Box, Text } from '@components/Restyle/index';
import Icon from '@components/shared/Icon/Icon';
import { LocalFile } from '@graphql/generated';
import { MAX_RECORDING_SECONDS } from '@hooks/useChatInput';

import { formatToMMSS } from '../../utils/voiceUtils';

enum FadeDirection {
  In = 1,
  Out = 0,
}

export const VoiceRecord = ({
  setShowRecording,
  voiceFileHandler,
}: {
  setShowRecording: (bool: boolean) => void;
  voiceFileHandler: (voice: LocalFile) => void;
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [isRecording, setIsRecording] = useState(false);
  const [recordTime, setRecordTime] = useState(0);

  const fadeAnim = useRef(new Animated.Value(1)).current;
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stoppingRef = useRef(false);

  const recorder = useAudioRecorder(RecordingPresets.HIGH_QUALITY, (status) => {
    if (!status.isFinished && !status.hasError) {
      const seconds = Math.floor(recorder.currentTime);
      setRecordTime(seconds);
    }
  });

  const fade = (direction: FadeDirection) => {
    Animated.timing(fadeAnim, {
      toValue: direction,
      duration: 1000,
      useNativeDriver: true,
    }).start();
  };

  useEffect(() => {
    if (isRecording) {
      timerRef.current = setInterval(() => {
        const seconds = Math.floor(recorder.currentTime);
        setRecordTime(seconds);
        if (seconds % 2 === 0) {
          fade(FadeDirection.Out);
        } else {
          fade(FadeDirection.In);
        }
      }, 500);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isRecording]);

  const startRecording = async () => {
    try {
      if (isRecording) {
        await stopRecording(true);
      }
      await setAudioModeAsync({
        playsInSilentMode: true,
        allowsRecording: true,
      });
      await recorder.prepareToRecordAsync();
      recorder.record();
      setIsRecording(true);
    } catch (err) {
      console.error('Failed to start recording', err);
    }
  };

  const getVoiceInfo = async (
    url: string,
    duration: number
  ): Promise<LocalFile | undefined> => {
    return Platform.OS === 'web'
      ? await getVoiceInfoWeb(url, duration)
      : await getVoiceInfoMobile(url, duration);
  };

  const getVoiceInfoWeb = async (
    url: string,
    duration: number
  ): Promise<LocalFile | undefined> => {
    const clientId = uuid.v4().toString();
    const size = 0;
    const contentType = 'audio/webm';

    return {
      __typename: 'LocalFile',
      id: clientId,
      url,
      name: new Date().toISOString(),
      size,
      contentType,
      clientId,
      isImage: false,
      cdnBaseUrl: '',
      path: '',
      isAudio: true,
      duration: Math.floor(duration),
      isVideo: false,
      thumbnail: '',
      isPreviewable: false,
    };
  };

  const getVoiceInfoMobile = async (
    url: string,
    duration: number
  ): Promise<LocalFile | undefined> => {
    const clientId = uuid.v4().toString();
    let size = 0;
    const contentType = 'audio/m4a';

    const info = await FileSystem.getInfoAsync(url);
    size = info.size ? info.size : 0;

    const fileName = `${clientId}.m4a`;

    // Not a dynamic import('react-native-fs'): in dev that is fetched as a
    // lazy bundle and fails with LoadBundleFromServerRequestError (TA-11049).
    const destUri = `${FileSystem.documentDirectory}${fileName}`;
    try {
      await FileSystem.copyAsync({ from: url, to: destUri });
    } catch (err) {
      console.error('Cache image failed:', err);
    }

    return {
      __typename: 'LocalFile',
      id: clientId,
      url: destUri,
      name: fileName,
      size,
      contentType,
      clientId,
      isImage: false,
      cdnBaseUrl: '',
      path: '',
      isAudio: true,
      duration: Math.floor(duration),
      isVideo: false,
      thumbnail: '',
      isPreviewable: false,
    };
  };

  const stopRecording = async (save: boolean) => {
    if (stoppingRef.current) return;
    stoppingRef.current = true;
    setIsRecording(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    try {
      const capturedDuration = recorder.currentTime;
      await recorder.stop();
      await setAudioModeAsync({
        allowsRecording: false,
        playsInSilentMode: true,
      });

      if (Math.floor(capturedDuration) === 0 && save && Platform.OS !== 'web') {
        toast.show(t('models:chat.input.holdToRecordVoiceMessage'), {
          duration: 2000,
        });
      } else if (save && recorder.uri) {
        const voiceFile = await getVoiceInfo(recorder.uri, capturedDuration);
        if (voiceFile) {
          voiceFileHandler(voiceFile);
        }
      }
    } catch (err) {
      console.error('Failed to stop recording', err);
    } finally {
      stoppingRef.current = false;
      setRecordTime(0);
      setShowRecording(false);
    }
  };

  useEffect(() => {
    if (recordTime >= MAX_RECORDING_SECONDS) {
      stopRecording(true);
    }
  }, [recordTime]);
  return (
    <Box
      flexDirection='row'
      alignItems='center'
      marginTop='m'
      marginBottom='m'
      marginHorizontal='l'
      justifyContent='space-between'>
      <Box alignItems='center' flexDirection='row'>
        {isRecording && (
          <Animated.View style={{ opacity: fadeAnim }}>
            <Icon
              name='RecordingCircle'
              width={8}
              height={8}
              marginRight='xs'
            />
          </Animated.View>
        )}
        <TouchableOpacity onPress={() => setShowRecording(false)}>
          <Text variant='body1' color='greenSecondary'>
            {isRecording
              ? formatToMMSS(recordTime)
              : t('models:chat.input.cancel')}
          </Text>
        </TouchableOpacity>
      </Box>
      {isRecording && (
        <Box
          style={{
            // absolute so that record time changing does not affect position
            ...StyleSheet.absoluteFillObject,
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <Text variant='body1' color='grey04' textAlign='center'>
            {t('models:chat.input.maxRecordingTime')}
          </Text>
        </Box>
      )}
      <Icon
        accessibilityLabel='Recording'
        name='Recording'
        width={54}
        height={54}
        color='black'
        onPressIn={startRecording}
        onPressOut={() => isRecording && stopRecording(false)}
        onPress={() => isRecording && stopRecording(true)}
        delayPressOut={500}
      />
    </Box>
  );
};
